import {
  HTMLAttributes,
  ReactElement,
  forwardRef,
  useCallback,
  useRef,
  useState,
} from "react";

export interface CollapseProps extends HTMLAttributes<HTMLDivElement> {
  in: boolean;
  unmountOnClose?: boolean;
  children: ReactElement;
  dimension?: "width" | "height";
}

const Collapse = forwardRef<HTMLDivElement, CollapseProps>((props, ref) => {
  const {
    in: show,
    children,
    dimension = "height",
    unmountOnClose = false,
    ...rest
  } = props;

  const [height, setHeight] = useState("auto");
  const [width, setWidth] = useState("auto");

  const resizeObserver = useRef<ResizeObserver | undefined>(undefined);

  const onRefChange = useCallback(
    (node: HTMLDivElement) => {
      if (!!resizeObserver.current) {
        resizeObserver.current.disconnect();
        resizeObserver.current = undefined;
      }

      if (!node) return;

      setHeight(`${node.scrollHeight}px`);
      setWidth(`${node.scrollWidth}px`);

      resizeObserver.current = new ResizeObserver(() => {
        setHeight(`${node.scrollHeight}px`);
        setWidth(`${node.scrollWidth}px`);
      });
      resizeObserver.current.observe(node);
    },
    [show]
  );

  return (
    <div
      {...rest}
      ref={ref}
      style={{
        height: dimension === "height" ? (show ? height : 0) : "auto",
        width: dimension === "width" ? (show ? width : 0) : "auto",
      }}
      className="transition-all duration-300 transform overflow-hidden"
    >
      <div ref={onRefChange}>
        {(show || (!show && !unmountOnClose)) && children}
      </div>
    </div>
  );
});

export default Collapse;
